import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFeature, useVendorConfig } from '../context/VendorConfigContext';
import { Box, CircularProgress, Alert } from '@mui/material';

interface VendorFeatureRouteProps {
  children: JSX.Element;
  feature: 'tableManagement' | 'floorManagement' | 'sectionManagement' | 'multipleMenus' | 'kitchenDisplay' | 'orderLines' | 'stewardFeatures' | 'loyaltyPrograms' | 'tipsDistribution';
  redirectTo?: string;
}

/**
 * Route guard component that enforces vendor feature access control.
 *
 * Performance characteristics:
 * - Configuration fetched ONCE at login (see VendorConfigContext.tsx line 84-88)
 * - Stored in React Context (in-memory)
 * - This component performs instant boolean check (no API call)
 * - Zero overhead on subsequent page navigations
 *
 * Usage:
 * <Route path="/vendor/tables" element={
 *   <PrivateRoute>
 *     <VendorFeatureRoute feature="tableManagement">
 *       <Visual2DLayoutManager />
 *     </VendorFeatureRoute>
 *   </PrivateRoute>
 * } />
 */
const VendorFeatureRoute: React.FC<VendorFeatureRouteProps> = ({
  children,
  feature,
  redirectTo = '/vendor/orders'
}) => {
  const { isVendor, isAdmin, user } = useAuth();
  const { loading } = useVendorConfig();
  const hasFeature = useFeature(feature);

  // Check if user is a steward
  const isSteward = user?.roles?.some(role => role === 'ROLE_STEWARD');

  // Admin users bypass all feature checks
  if (isAdmin) {
    console.log(`✓ [Route Guard] Admin access granted for feature: ${feature}`);
    return children;
  }

  // Stewards can access kitchenDisplay feature (they need to view kitchen orders)
  if (isSteward && feature === 'kitchenDisplay') {
    console.log(`✓ [Route Guard] Steward access granted for feature: ${feature}`);
    return children;
  }

  // Non-vendor users shouldn't be here (handled by PrivateRoute)
  if (!isVendor) {
    console.log(`✗ [Route Guard] Non-vendor attempted to access feature: ${feature}`);
    return <Navigate to="/login" replace />;
  }

  // Show loading state while fetching vendor configuration
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Check feature access (instant boolean check - no API call)
  if (!hasFeature) {
    console.log(`L [Route Guard] Feature disabled: ${feature} - redirecting to ${redirectTo}`);
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          This feature is not enabled for your account. Please contact support or check your vendor configuration.
        </Alert>
      </Box>
    );
  }

  console.log(` [Route Guard] Feature access granted: ${feature}`);
  return children;
};

export default VendorFeatureRoute;
